import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useState } from "react"
import { shallowEqual } from "react-redux"
import { useAppDispatch, useAppSelector } from "../../../../store/hooks"
import {
    selectCurrentArticlesFilter,
    selectKnowledgeBaseWorkMode,
    selectSearchArticlesState
} from "../../../../store/knowledgeBase/selectors"
import { setCurrentArticlesFilter, setKnowledgeBaseWorkMode } from "../../../../store/knowledgeBase/slice"
import { KnowledgeBaseWorkMode } from "../../../../models/knowledgeBase/workMode"
import { SearchArticlesRequest } from "../../../../models/article"
import { CommonSearchArticlesInputProps, SearchArticlesInput } from "../.."
import { ArticleFilterFormValues } from "../../../../models/articleFilter"

interface SearchArticlesInputLogicProps extends CommonSearchArticlesInputProps {
    onSearch: (request: SearchArticlesRequest) => void
}

const ENTER_KEY_CODE = 13
export const MIN_QUERY_LENGTH_TO_SEARCH = 3

const isFiltersEnabled = (articlesFilter: ArticleFilterFormValues) => !!Object.keys(articlesFilter).length

export const SearchArticlesInputLogic = ({ onSearch, ...props }: SearchArticlesInputLogicProps) => {
    const dispatch = useAppDispatch()
    const searchArticlesState = useAppSelector(selectSearchArticlesState, shallowEqual)
    const workMode = useAppSelector(selectKnowledgeBaseWorkMode)
    const articlesFilter = useAppSelector(selectCurrentArticlesFilter)
    const [query, setQuery] = useState<string>("")
    const [enableRemoved, setEnableRemoved] = useState<boolean>(false)

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const query = e.target.value
            setQuery(query)
            if (!query && !isFiltersEnabled(articlesFilter)) {
                dispatch(setKnowledgeBaseWorkMode(KnowledgeBaseWorkMode.CatalogsView))
            }
        },
        [dispatch, articlesFilter]
    )

    const handleClear = useCallback(() => {
        setQuery("")
        dispatch(setKnowledgeBaseWorkMode(KnowledgeBaseWorkMode.CatalogsView))
        dispatch(setCurrentArticlesFilter({}))
    }, [dispatch])

    const handleSearch = useCallback(() => {
        const isSearchMode = workMode === KnowledgeBaseWorkMode.Search
        const isQueryEmpty = !query && !isFiltersEnabled(articlesFilter)
        if (isSearchMode && isQueryEmpty) {
            dispatch(setKnowledgeBaseWorkMode(KnowledgeBaseWorkMode.CatalogsView))
            return
        }

        const isCatalogMode = workMode === KnowledgeBaseWorkMode.CatalogsView
        const isQueryValid = query.length >= MIN_QUERY_LENGTH_TO_SEARCH
        if (isCatalogMode && isQueryValid) {
            dispatch(setKnowledgeBaseWorkMode(KnowledgeBaseWorkMode.Search))
            return
        }

        onSearch(
            query
                ? {
                      ...articlesFilter,
                      Text: query,
                      EnableRemoved: enableRemoved
                  }
                : articlesFilter
        )
    }, [articlesFilter, dispatch, enableRemoved, onSearch, query, workMode])

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            switch (e.keyCode) {
                case ENTER_KEY_CODE:
                    handleSearch()
                    break
                default:
                    break
            }
        },
        [handleSearch]
    )

    const handleRemoved = () => {
        setEnableRemoved(currValue => !currValue)
        handleSearch()
    }

    useEffect(() => {
        handleSearch()
    }, [articlesFilter, handleSearch])

    return (
        <SearchArticlesInput
            {...props}
            query={query}
            enableRemoved={enableRemoved}
            onEnableRemoved={handleRemoved}
            loading={searchArticlesState.inProcess}
            onSearch={handleSearch}
            onClear={handleClear}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            isFiltersEnabled={isFiltersEnabled(articlesFilter)}
        />
    )
}
